/* OnSumn — Filter Popup
 *
 * MINIMALIST REFINEMENT — 2026-06-02 (impeccable:craft)
 * ------------------------------------------------------
 * Distill cut Cities. This pass restrained the visual story.
 *
 * What changed and why:
 *  - Mono-accent chip system. Pre: 14 chip colors (6 cat + 4 date + 4 time)
 *    competing for the eye. Post: every selected chip uses a single amber
 *    accent. 60-30-10 rule says accents only work BECAUSE they're rare —
 *    14 accents = 0 accents. Category colors live ONLY in the small
 *    "selected" indicator dot at the chip's leading edge.
 *  - Cut the category-hint grid. "Music · Shows · DJs · open mics" × 6
 *    read like a glossary. The chip labels (Music, Cards, Art, Food,
 *    Nightlife, Community) are already self-evident — descriptions added
 *    a third visual layer for zero clarity gain.
 *  - Stripped chip shell. No more border-thickness changes, no scale
 *    transforms, no glow shadows. Selected = filled amber. Unselected =
 *    flat ghost. Hover = subtle background bump. Honest interactions.
 *  - Quieter typography. Section labels switched from 10px bold uppercase
 *    with letter-spacing to 13px regular Be Vietnam Pro, lowercase, lighter
 *    color. Murmurs, not signposts.
 *  - Generous spacing. Section gap 22px → 36px. Chip gap 8px → 10px.
 *    Internal padding 18px/22px → 24px/28px. Room for the eye to land.
 *  - Action row simpler. Wipe It stays ghost. Lock It In keeps orange
 *    (the CTA is the ONE place orange earns its weight) but loses the
 *    hover translate + heavy shadow.
 *
 * Kept everything functional: all 4 sections (Cat + Sound + Date + Time),
 * progressive disclosure of Sound under Music, focus trap, Esc, 44px hit
 * targets, restore-on-close. No data model changes.
 *
 * Color = identity → color = signal. Brutalist-editorial minimalism.
 */

/* ── Selector Chips — mono-accent. Selected chips fill amber; an
 *    optional colorMap shows up as a small leading dot on UNSELECTED chips.
 *
 *    Dot rule (clarified 2026-06-02): dots only appear when the color
 *    means something OUTSIDE this popup. Category chips get dots because
 *    Music=orange, Food=amber, etc. are site-wide identities (the same
 *    chip color appears on event cards across the homepage). Date/Time/
 *    Sound chips DON'T get dots because their colors would be arbitrary
 *    — "Today is orange" carries no meaning, it's just decoration. */
function SelectorChips({ options, selected, onToggle, colorMap, darkMode, compact }) {
  // Single accent for ALL selected states. Amber-readable on dark; burnt
  // amber on light. The label ink flips with it: white on #D57800 is only
  // 3.22:1, dark ink there is 5.74:1; white on #8B4A00 is 6.82:1.
  const accent = darkMode ? '#D57800' : '#8B4A00';
  const accentFg = darkMode ? 'var(--on-international-orange)' : '#fff';
  const ghostBg = darkMode ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.025)';
  const ghostHoverBg = darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';
  const ghostBorder = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const ghostFg = darkMode ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.7)';

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 12,
    }}>
      {options.map(option => {
        const isSelected = selected.includes(option);
        const dotColor = colorMap?.[option] || null;

        return (
          <button
            key={option}
            onClick={() => onToggle(option)}
            aria-pressed={isSelected}
            className="focus-double-ring"
            onMouseEnter={(e) => {
              if (!isSelected) e.currentTarget.style.background = ghostHoverBg;
            }}
            onMouseLeave={(e) => {
              if (!isSelected) e.currentTarget.style.background = ghostBg;
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              gap: 9,
              padding: compact ? '10px 18px' : '13px 22px',
              minHeight: compact ? 42 : 50,
              borderRadius: 100,
              fontFamily: 'var(--font-body)',
              fontSize: compact ? 13 : 15,
              fontWeight: isSelected ? 700 : 500,
              letterSpacing: '0.01em',
              // No uppercase. Sentence case feels quieter, more confident.
              cursor: 'pointer',
              border: `1px solid ${isSelected ? accent : ghostBorder}`,
              background: isSelected ? accent : ghostBg,
              color: isSelected ? accentFg : ghostFg,
              transition: 'background 180ms ease, border-color 180ms ease, color 180ms ease',
            }}
          >
            {dotColor && !isSelected && (
              <span aria-hidden="true" style={{
                width: 7, height: 7, borderRadius: '50%',
                background: dotColor,
                flex: '0 0 auto',
                opacity: 0.85,
              }} />
            )}
            <span>{option}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Time-of-day chips ── */
const TIME_OPTIONS = ['Morning', 'Afternoon', 'Evening', 'Late Night'];
const TIME_COLORS = {
  'Morning': '#D57800',
  'Afternoon': '#FF4F00',
  'Evening': '#395542',
  'Late Night': '#7A99AC',
};

const DATE_OPTIONS = ['Today', 'Tomorrow', 'This Weekend', 'This Week'];
const DATE_COLORS = {
  'Today': '#FF4F00',
  'Tomorrow': '#D57800',
  'This Weekend': '#395542',
  'This Week': '#7A99AC',
};

/* ── Section label — quiet, lowercase, sentence-case murmur.
 *    Pre: 10px bold uppercase wide-letter-spacing signpost.
 *    Post: 15px regular sentence-case, generous margin below. */
function FilterLabel({ children, darkMode }) {
  return (
    <label style={{
      fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500,
      letterSpacing: '0',
      color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)',
      display: 'block', marginBottom: 16,
    }}>{children}</label>
  );
}

/* ── Filter Popup ── */
function FilterPopup({ darkMode, radius, activeFilters, onApply, onClose, visible }) {
  const [selectedCats, setSelectedCats] = React.useState(activeFilters?.categories || []);
  const [selectedTimes, setSelectedTimes] = React.useState(activeFilters?.times || []);
  const [selectedSounds, setSelectedSounds] = React.useState(activeFilters?.sounds || []);
  const [selectedDates, setSelectedDates] = React.useState(activeFilters?.dates || []);

  // Focus management: capture the trigger before opening so we can restore
  // focus to it on close (Iris a11y floor — modal close should not strand
  // keyboard users at document.body). panelRef anchors the focus trap.
  const triggerRef = React.useRef(null);
  const closeBtnRef = React.useRef(null);
  const panelRef = React.useRef(null);

  // onClose-via-ref so the open-effect doesn't re-run on every parent render
  // (parents typically pass an inline arrow as onClose). Pattern matches
  // the latest-callback idiom — onClose stays current without invalidating
  // the effect dep that adds/removes the window keydown listener.
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Reset internal state when popup opens; capture trigger; move focus into
  // the dialog. Esc closes (delegated to global keydown so it works even when
  // focus has drifted inside the panel). Tab / Shift+Tab wrap focus within
  // the dialog so keyboard users can't tab out into the inert background.
  React.useEffect(() => {
    if (visible) {
      setSelectedCats(activeFilters?.categories || []);
      setSelectedTimes(activeFilters?.times || []);
      setSelectedSounds(activeFilters?.sounds || []);
      setSelectedDates(activeFilters?.dates || []);

      triggerRef.current = (typeof document !== 'undefined') ? document.activeElement : null;
      // Defer focus move until after the open transition starts so the close
      // button is already in the layout.
      const focusTimer = setTimeout(() => {
        if (closeBtnRef.current) closeBtnRef.current.focus();
      }, 50);

      const FOCUSABLE_SELECTOR = [
        'button:not([disabled])',
        'a[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      const handleKey = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCloseRef.current();
          return;
        }
        if (e.key !== 'Tab') return;
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = panel.querySelectorAll(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const insidePanel = panel.contains(active);
        if (e.shiftKey) {
          // Shift+Tab from first (or from outside the panel) → focus last.
          if (active === first || !insidePanel) {
            e.preventDefault();
            last.focus();
          }
        } else {
          // Tab from last (or from outside the panel) → focus first.
          if (active === last || !insidePanel) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      window.addEventListener('keydown', handleKey);

      return () => {
        clearTimeout(focusTimer);
        window.removeEventListener('keydown', handleKey);
      };
    } else if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
      // Restore focus to the trigger when closing.
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [visible]);

  const toggleItem = (list, setList) => (item) => {
    setList(prev =>
      prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]
    );
  };

  const musicSelected = selectedCats.includes('Music');

  const totalSelected = selectedCats.length + selectedTimes.length + selectedSounds.length + selectedDates.length;

  const handleClear = () => {
    setSelectedCats([]);
    setSelectedTimes([]);
    setSelectedSounds([]);
    setSelectedDates([]);
  };

  const handleApply = () => {
    onApply({
      categories: selectedCats,
      times: selectedTimes,
      sounds: musicSelected ? selectedSounds : [],
      dates: selectedDates,
    });
    onClose();
  };

  // Derived from lib/category-colors.js — display labels here are mixed-case
  // ("Music"/"Art"), canonical keys in window.CATEGORY_COLORS are UPPERCASE,
  // so we map at lookup time. Pre-fix this file had completely different
  // hexes than Flows + Grid (MUSIC was amber here, orange everywhere else).
  const canonicalColors = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
    'MUSIC': '#FF4F00', 'CARDS': '#7A99AC', 'ART': '#5A7A68',
    'FOOD': '#D57800', 'NIGHTLIFE': '#7C5BBC', 'COMMUNITY': '#395542',
  };
  const categoryColors = {
    'Music':     canonicalColors.MUSIC,
    'Cards':     canonicalColors.CARDS,
    'Art':       canonicalColors.ART,
    'Food':      canonicalColors.FOOD,
    'Nightlife': canonicalColors.NIGHTLIFE,
    'Community': canonicalColors.COMMUNITY,
  };

  // No divider in minimalist refinement — generous whitespace replaces
  // the line. The eye doesn't need a fence between What and When; gap
  // alone is enough hierarchy.
  const sectionGap = { height: 44 };
  const subSectionGap = { height: 24 };

  return (
    <React.Fragment>
      {/* Backdrop — presentational scrim. No aria-label/role: aria-label is
          prohibited on a role-less <div> (aria-prohibited-attr), and the real
          "Close filters" button inside the dialog already exposes the action
          to assistive tech. Click-to-dismiss is a mouse convenience only. */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, zIndex: 9990,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Panel — a dialog only while visible. The popup stays mounted hidden
          for its 250 ms fade, and a hidden dialog must not read as an open
          modal: three stand-down guards (CalendarView ×2, SearchBar) key on
          [aria-modal="true"] and would never fire on index.html otherwise.
          The name goes with the role: aria-label on a role-less div is an
          axe aria-prohibited-attr hit (the backdrop note above), and the
          Lighthouse CI a11y budget runs on this page. */}
      <div
        ref={panelRef}
        role={visible ? 'dialog' : undefined}
        aria-modal={visible ? 'true' : undefined}
        aria-label={visible ? 'Filter events' : undefined}
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: visible
            ? 'translate(-50%, -50%) scale(1)'
            : 'translate(-50%, -48%) scale(0.97)',
          zIndex: 9991,
          // Laptop fix: 620px on desktop (was 440 — too small on a 1440 screen).
          // Mobile preserved: 94vw caps at ~370px on a 390-wide iPhone.
          width: 'min(620px, 94vw)',
          maxHeight: '86vh',
          overflowY: 'auto',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
          transition: 'opacity 0.25s ease, transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          ...glassStyle(darkMode, 'panel'),
          background: darkMode
            ? 'linear-gradient(145deg, rgba(18,20,20,0.97), rgba(10,10,10,0.98))'
            : 'linear-gradient(145deg, rgba(255,255,255,0.97), rgba(248,248,248,0.98))',
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
          borderRadius: Math.min(radius, 24),
          padding: 0,
        }}>
        {/* Header — generous padding, quieter counter */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '32px 36px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
            <h3 style={{
              fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800,
              color: darkMode ? '#fff' : '#0A0A0A',
              letterSpacing: '-0.025em',
              margin: 0,
            }}>Dial it in</h3>
            {totalSelected > 0 && (
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                color: darkMode ? 'rgba(213,120,0,0.9)' : 'rgba(139,74,0,0.9)',
              }}>{totalSelected} on</span>
            )}
          </div>
          {/* 44×44 hit area, 32px visible chip — WCAG 2.5.8 / Iris floor.
              Receives initial focus on open (see useEffect above) so keyboard
              users can dismiss with Enter/Space immediately, or Esc. */}
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close filters"
            className="focus-double-ring"
            onMouseEnter={(e) => {
              const chip = e.currentTarget.firstElementChild;
              if (chip) chip.style.background = darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              const chip = e.currentTarget.firstElementChild;
              if (chip) chip.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
            }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <span aria-hidden="true" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              fontSize: 18, lineHeight: 1,
              width: 32, height: 32, borderRadius: '50%',
              transition: 'background 0.15s',
            }}>×</span>
          </button>
        </div>

        {/* Filter sections — generous padding, no dividers, single accent.
            What → When sequence preserved from distill pass. Laptop-bumped
            internal padding so the wider panel doesn't feel hollow. */}
        <div style={{ padding: '28px 36px 36px' }}>
          {/* WHAT — Categories (+ nested Sounds when Music is selected) */}
          <div>
            <FilterLabel darkMode={darkMode}>what's your move</FilterLabel>
            <SelectorChips
              options={['Music', 'Cards', 'Art', 'Food', 'Nightlife', 'Community']}
              selected={selectedCats}
              onToggle={toggleItem(selectedCats, setSelectedCats)}
              colorMap={categoryColors}
              darkMode={darkMode}
            />

            {/* Sub-genre expand when Music is selected — progressive disclosure
                preserved. No FilterLabel above the sub-chips; the indent +
                expand is enough hierarchy without a second murmur. */}
            <div style={{
              maxHeight: musicSelected ? 280 : 0,
              opacity: musicSelected ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease, margin-top 200ms ease',
              marginTop: musicSelected ? 14 : 0,
            }}>
              <SelectorChips
                options={window.TASTE_SOUNDS || ['EDM', 'R&B', 'Hip-Hop', 'Rock', 'Jazz', 'Pop', 'Latin', 'Indie']}
                selected={selectedSounds}
                onToggle={toggleItem(selectedSounds, setSelectedSounds)}
                darkMode={darkMode}
                compact
              />
            </div>
          </div>

          <div style={sectionGap} />

          {/* WHEN — split into two clearly labeled sub-sections so the
              eight chips don't read as one cluttered mass. Pre: stacked
              rows under one "when you pulling up" label = 8 chips with
              no hierarchy. Post: "when" (Date) and "what time" (Time),
              each with breathing room. Honest separation. */}
          <div>
            <FilterLabel darkMode={darkMode}>when</FilterLabel>
            <SelectorChips
              options={DATE_OPTIONS}
              selected={selectedDates}
              onToggle={toggleItem(selectedDates, setSelectedDates)}
              darkMode={darkMode}
            />
          </div>

          <div style={subSectionGap} />

          <div>
            <FilterLabel darkMode={darkMode}>what time</FilterLabel>
            <SelectorChips
              options={TIME_OPTIONS}
              selected={selectedTimes}
              onToggle={toggleItem(selectedTimes, setSelectedTimes)}
              darkMode={darkMode}
            />
          </div>

          {/* Action row. Orange is now the ONLY accent in the popup that
              isn't amber — the CTA earns its color because it's the single
              terminal action. No hover translate, no heavy shadow. Honest. */}
          <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
            {totalSelected > 0 && (
              <button
                onClick={handleClear}
                className="focus-double-ring"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
                  e.currentTarget.style.color = darkMode ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.72)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.58)';
                }}
                style={{
                  flex: 1,
                  padding: '17px 20px', minHeight: 54,
                  borderRadius: Math.min(radius, 14),
                  background: 'transparent',
                  border: 'none',
                  color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.58)',
                  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
                  letterSpacing: '0.01em',
                  cursor: 'pointer',
                  transition: 'background 180ms ease, color 180ms ease',
                }}>Wipe it</button>
            )}
            <button
              onClick={handleApply}
              className="focus-double-ring"
              style={{
                flex: totalSelected > 0 ? 2 : 1,
                padding: '17px 20px', minHeight: 54,
                borderRadius: Math.min(radius, 14),
                background: 'var(--international-orange)',
                border: 'none',
                color: 'var(--on-international-orange)',
                fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                transition: 'background 180ms ease, filter 180ms ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
            >
              {totalSelected > 0 ? `Lock it in · ${totalSelected}` : 'Show me everything'}
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

FilterPopup.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  activeFilters: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};

SelectorChips.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  colorMap: PropTypes.object,
  darkMode: PropTypes.bool.isRequired,
  compact: PropTypes.bool,
};

window.FilterPopup = FilterPopup;
window.SelectorChips = SelectorChips;
